import { join, joinQueryParams } from 'district-ui-client/utils/uri'
import { keepLatest } from 'reactiveweb/keep-latest'
import { trackedFunction } from 'reactiveweb/function'
import { use } from 'ember-resources'
import * as yup from 'yup'
import config from 'district-ui-client/config/environment'
import Service, { service } from '@ember/service'
import type { Log } from '@blakeelearning/log'
import type SessionService from 'district-ui-client/services/session'
import { queryParam } from 'district-ui-client/decorators/query-param'
import { isPresent } from '@ember/utils'

const LessonLevelDomainSchema = yup.object({
  id: yup.string().required(),
  title: yup.string().required(),
  grade: yup.number().required(),
  domain_code: yup.string().optional(),
  re_grade_position: yup.number().required(),
  curriculum: yup.string().optional(),
})

const ResponseSchema = yup.array(LessonLevelDomainSchema).required()

export type LessonLevelDomain = yup.InferType<typeof LessonLevelDomainSchema>

export type LessonLevelDomainResponse = yup.InferType<typeof ResponseSchema>

/**
 * Service to fetch and manage the list of MX lesson level domains.
 *
 * Get all lesson level domains via the `records` property.
 * Get all lesson level domain ids via the `allLessonLevelDomainIds` property.
 * Get the selected lesson level domain ids via the `selectedLessonLevelDomainIds` property.
 *
 * The `selectedLessonLevelDomainIds` property is bound to the `reporting/ui-scope/mx` controller's
 * `lessonLevelDomainIds` query param, which is used to filter results in reports via
 * the filter.lesson-level-domain-codes gravity report query param.
 *
 * @class MxLessonLevelDomainService
 * @extends Service
 *
 **/
export default class MxLessonLevelDomainService extends Service {
  @service log!: Log

  @service session!: SessionService

  @queryParam('reporting/ui-scope/mx') lessonLevelDomainIds?: string[]

  get selectedLessonLevelDomainIds(): string[] {
    return this.lessonLevelDomainIds ?? []
  }

  get allLessonLevelDomainIds(): string[] {
    return this.records.map((record) => record.id)
  }

  get records(): LessonLevelDomain[] {
    return this.data?.filter((record) => isPresent(record.domain_code)) ?? []
  }

  @use
  data = keepLatest({
    value: () => this.fetchData.value,
    when: () => this.fetchData.isPending,
  })

  fetchData = trackedFunction(this, async (): Promise<LessonLevelDomain[]> => {
    const curriculum = this.curriculum
    if (curriculum === undefined) {
      this.log.debug('No curriculum set, cannot fetch mx grade domain list')
      return []
    }

    const url = this.buildUrl(curriculum)

    this.log.debug(`Fetching mx grade domain list from ${url}`)

    try {
      const response = await this.fetch(url)
      if (response.ok) {
        const data: unknown = await response.json()
        return await ResponseSchema.validate(data)
      } else {
        this.log.error('Failed to fetch mx grade domain list', JSON.stringify(response.status), url)
        return []
      }
    } catch (e: unknown) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        // Fetch aborted by user action, eg closed tab, stop button, network outage. Do nothing.
      } else {
        this.log.error('Failed to fetch mx grade domain list', e || '', url)
      }
      return []
    }
  })

  private buildUrl(curriculum: string): string {
    const path = join(config.lessonsApiV1Url, 'mathletics/grade_domain')
    return joinQueryParams(path, {
      curriculum,
    }).toString()
  }

  async fetch(...args: Parameters<typeof fetch>) {
    return fetch(...args)
  }

  private get curriculum() {
    return this.session.currentDistrict.standardsSet?.name
  }
}

declare module '@ember/service' {
  interface Registry {
    'mx-lesson-level-domain': MxLessonLevelDomainService
  }
}
